import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Landmark, Calendar as CalendarIcon, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { formatCurrency, formatDate } from '../../lib/utils';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BalanceSheet = () => {
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBalanceSheet();
  }, [selectedDate]);

  const fetchBalanceSheet = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/finance/balance-sheet?as_of_date=${dateStr}`);
      setBalanceSheet(response.data);
    } catch (error) {
      console.error('Failed to fetch balance sheet:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded animate-pulse w-48" />
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-8 bg-slate-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="balance-sheet-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Balance Sheet</h1>
          <p className="text-slate-600 mt-1">Financial position statement</p>
        </div>
        <div className="flex gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="balance-date-picker">
                <CalendarIcon className="w-4 h-4 mr-2" />
                As of {format(selectedDate, 'PPP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {balanceSheet && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-slate-900">ACADEMION SCHOOL</h2>
            <p className="text-slate-600">Balance Sheet</p>
            <p className="text-sm text-slate-500">As of {formatDate(balanceSheet.as_of_date)}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Assets */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 mb-4">ASSETS</h3>
              <div className="space-y-2">
                {balanceSheet.assets.items.map((item, index) => (
                  <div key={index} className="flex justify-between py-1">
                    <span className="text-slate-700">{item.code} - {item.name}</span>
                    <span className="font-mono text-slate-900">{formatCurrency(item.balance)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between py-2 mt-4 border-t-2 border-slate-900 font-bold">
                <span>Total Assets</span>
                <span className="font-mono">{formatCurrency(balanceSheet.assets.total)}</span>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 mb-4">LIABILITIES</h3>
              <div className="space-y-2">
                {balanceSheet.liabilities.items.map((item, index) => (
                  <div key={index} className="flex justify-between py-1">
                    <span className="text-slate-700">{item.code} - {item.name}</span>
                    <span className="font-mono text-slate-900">{formatCurrency(item.balance)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between py-2 mt-2 border-t border-slate-300">
                <span className="font-semibold">Total Liabilities</span>
                <span className="font-mono font-semibold">{formatCurrency(balanceSheet.liabilities.total)}</span>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 mb-4 mt-8">EQUITY</h3>
              <div className="space-y-2">
                {balanceSheet.equity.items.map((item, index) => (
                  <div key={index} className="flex justify-between py-1">
                    <span className="text-slate-700">{item.code} - {item.name}</span>
                    <span className="font-mono text-slate-900">{formatCurrency(item.balance)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between py-2 mt-2 border-t border-slate-300">
                <span className="font-semibold">Total Equity</span>
                <span className="font-mono font-semibold">{formatCurrency(balanceSheet.equity.total)}</span>
              </div>

              <div className="flex justify-between py-2 mt-4 border-t-2 border-slate-900 font-bold">
                <span>Total Liabilities & Equity</span>
                <span className="font-mono">{formatCurrency(balanceSheet.total_liabilities_and_equity)}</span>
              </div>
            </div>
          </div>

          {/* Balance Check */}
          <div className="mt-8 p-4 rounded-lg bg-slate-50 border border-slate-200">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-700">Balance Check:</span>
              <span className={`font-mono font-bold ${
                Math.abs(balanceSheet.assets.total - balanceSheet.total_liabilities_and_equity) < 0.01
                  ? 'text-emerald-600'
                  : 'text-red-600'
              }`}>
                {Math.abs(balanceSheet.assets.total - balanceSheet.total_liabilities_and_equity) < 0.01
                  ? '✓ Balanced'
                  : `✗ Difference: ${formatCurrency(balanceSheet.assets.total - balanceSheet.total_liabilities_and_equity)}`
                }
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceSheet;
